import { useMemo } from 'react'
import { useIntl, type IntlShape } from 'react-intl'
import { getPlural } from 'lib/string/pluralize'
import { capitalise } from 'lib/string/stringUtils'

interface FormatSaleUnitLabelOptions {
  withLeadingSlash?: boolean
}

interface FormatSaleUnitLabelFn {
  (saleUnit: string, options?: FormatSaleUnitLabelOptions): string
  (saleUnit?: string, options?: FormatSaleUnitLabelOptions): string | undefined
}

/**
 * Returns an ICU-pluralized "count + saleUnit" string (e.g. "2 rooms", "1 villa")
 * for all known sale unit values, with a getPlural fallback for unknown units.
 *
 * Use the hook version (`useSaleUnitFormatting`) in React components.
 * Use this standalone function in selectors/utils that already receive `intl`.
 */
export function formatSaleUnitCount(intl: IntlShape, saleUnit: string, count: number): string {
  const key = saleUnit.toLowerCase()
  const startsWithCapital = saleUnit.length > 0 && saleUnit[0] === saleUnit[0].toUpperCase() && saleUnit[0] !== saleUnit[0].toLowerCase()

  let result: string | undefined

  if (key === 'room') {
    result = intl.formatMessage({
      id: 'nLcmGau2j6XBvHXOKwC6',
      defaultMessage: '{count, plural, one {# room} other {# rooms}}',
      description: 'Pluralised sale unit label showing the number of rooms in the hotel offer pricing',
    }, { count })
  } else if (key === 'night') {
    result = intl.formatMessage({
      id: 'h3_iVk6wVBAnOg1R2eny',
      defaultMessage: '{count, plural, one {# night} other {# nights}}',
      description: 'Pluralised sale unit label showing the number of nights in the offer pricing',
    }, { count })
  } else if (key === 'villa') {
    result = intl.formatMessage({
      id: 'VWLEz2TZE_v27KGj7_p2',
      defaultMessage: '{count, plural, one {# villa} other {# villas}}',
      description: 'Pluralised sale unit label showing the number of villas in the offer pricing',
    }, { count })
  } else if (key === 'suite') {
    result = intl.formatMessage({
      id: 'iFxQNxrb3i1z963Dp1XJ',
      defaultMessage: '{count, plural, one {# suite} other {# suites}}',
      description: 'Pluralised sale unit label showing the number of suites in the offer pricing',
    }, { count })
  } else if (key === 'guest') {
    result = intl.formatMessage({
      id: 'WlE6FWgCVfrbvQPPIndu',
      defaultMessage: '{count, plural, one {# guest} other {# guests}}',
      description: 'Pluralised sale unit label showing the number of guests in the offer pricing',
    }, { count })
  } else if (key === 'adult') {
    result = intl.formatMessage({
      id: '6SlT4c8Aa4PXEwaHG0N6',
      defaultMessage: '{count, plural, one {# adult} other {# adults}}',
      description: 'Pluralised sale unit label showing the number of adults in the offer pricing',
    }, { count })
  } else if (key === 'traveller') {
    result = intl.formatMessage({
      id: '4gz2GNYpPhcRf2JMJtIZ',
      defaultMessage: '{count, plural, one {# traveller} other {# travellers}}',
      description: 'Sale unit count for travellers in the traveller details form',
    }, { count })
  } else if (key === 'person') {
    result = intl.formatMessage({
      id: '6-HH7f0_A5N4dtkBf-vX',
      defaultMessage: '{count, plural, one {# person} other {# people}}',
      description: 'Pluralised sale unit label showing the number of people in the offer pricing',
    }, { count })
  } else if (key === 'cabin') {
    result = intl.formatMessage({
      id: 'nwc7xwFhLB5fnrutCxLj',
      defaultMessage: '{count, plural, one {# cabin} other {# cabins}}',
      description: 'Pluralised sale unit label showing the number of cabins in the cruise pricing',
    }, { count })
  } else if (key === 'package') {
    result = intl.formatMessage({
      id: 'TR2vkZRKyP6-FXpBsPEv',
      defaultMessage: '{count, plural, one {# package} other {# packages}}',
      description: 'Pluralised sale unit label showing the number of packages in the offer pricing',
    }, { count })
  } else if (key === 'property') {
    result = intl.formatMessage({
      id: 'oD4BmJJv37z1mAnb_5df',
      defaultMessage: '{count, plural, one {# property} other {# properties}}',
      description: 'Sale unit count for properties for computed display labels',
    }, { count })
  }

  if (result) {
    if (startsWithCapital) {
      // Preserve capitalization of the unit word (e.g. "2 rooms" -> "2 Rooms")
      const spaceIdx = result.indexOf(' ')
      if (spaceIdx !== -1 && spaceIdx + 1 < result.length) {
        return result.slice(0, spaceIdx + 1) + result[spaceIdx + 1].toUpperCase() + result.slice(spaceIdx + 2)
      }
    }
    return result
  }

  // i18n: fallback for unknown sale units - uses getPlural (English-only)
  return count === 1 ?
      intl.formatMessage({
        id: 'UhfSXOtPHDYGUz7gvTVL',
        defaultMessage: '{count} {saleUnit}',
        description: 'Fallback singular sale unit count for unrecognized sale units',
      }, { count, saleUnit }) :
      intl.formatMessage({
        id: 't4acHgq11prf8rMioDjd',
        defaultMessage: '{count} {saleUnitPlural}',
        description: 'Fallback plural sale unit count for unrecognized sale units',
      }, { count, saleUnitPlural: getPlural(saleUnit) })
}

/**
 * Returns a localized plural noun for a known sale unit (e.g. "rooms", "villas", "people").
 * Falls back to getPlural for unknown units.
 *
 * Use the hook version (`useSaleUnitFormatting`) in React components.
 * Use this standalone function in selectors/utils that already receive `intl`.
 */
export function getSaleUnitPluralNoun(intl: IntlShape, saleUnit: string): string {
  const startsWithCapital = saleUnit.length > 0 && saleUnit[0] === saleUnit[0].toUpperCase() && saleUnit[0] !== saleUnit[0].toLowerCase()

  const known: Record<string, string> = {
    adult: intl.formatMessage({
      id: 'tq9Xh89UdyRc86xIHbsT',
      defaultMessage: 'adults',
      description: 'Plural sale unit noun for adults in offer and pricing messages',
    }),
    cabin: intl.formatMessage({
      id: 'snseQP9Ydp-vbAABoh-S',
      defaultMessage: 'cabins',
      description: 'Plural sale unit noun for cabins in offer and pricing messages',
    }),
    day: intl.formatMessage({
      id: 'o1CwBf1SkKYLgU7g0LX7',
      defaultMessage: 'days',
      description: 'Plural sale unit noun for days in offer and pricing messages',
    }),
    guest: intl.formatMessage({
      id: 'bxVtTbdimFHe_qt1Xzpd',
      defaultMessage: 'guests',
      description: 'Plural sale unit noun for guests in offer and pricing messages',
    }),
    night: intl.formatMessage({
      id: 'uZUM7IZFon9LJ0fqWGc9',
      defaultMessage: 'nights',
      description: 'Plural sale unit noun for nights in offer and pricing messages',
    }),
    package: intl.formatMessage({
      id: 'AaPkkqRkjUPbj1ioU4pd',
      defaultMessage: 'packages',
      description: 'Plural sale unit noun for packages in offer and pricing messages',
    }),
    person: intl.formatMessage({
      id: 'cgf7L7VO9jbta4G-F0Ag',
      defaultMessage: 'people',
      description: 'Plural sale unit noun for people in offer and pricing messages',
    }),
    property: intl.formatMessage({
      id: 'P7A5c_CUF5_3EnXKHpqG',
      defaultMessage: 'properties',
      description: 'Plural sale unit noun for properties in offer and pricing messages',
    }),
    rental: intl.formatMessage({
      id: '_0JxTUgUo8FajZr47IVa',
      defaultMessage: 'rentals',
      description: 'Plural sale unit noun for rentals in offer and pricing messages',
    }),
    room: intl.formatMessage({
      id: 'bM2VXSyEtBg54Ejh-Spt',
      defaultMessage: 'rooms',
      description: 'Plural sale unit noun for rooms in offer and pricing messages',
    }),
    suite: intl.formatMessage({
      id: 'fkaNvVOO9ATPi7xxc5Wa',
      defaultMessage: 'suites',
      description: 'Plural sale unit noun for suites in offer and pricing messages',
    }),
    traveler: intl.formatMessage({
      id: 'XZZfQT03fv9oXzFKNh9Y',
      defaultMessage: 'travelers',
      description: 'Plural sale unit noun for travelers in offer and pricing messages',
    }),
    traveller: intl.formatMessage({
      id: 'EzPLAfvOJ1s9hyj7r5H8',
      defaultMessage: 'travellers',
      description: 'Plural sale unit noun for travellers in offer and pricing messages',
    }),
    villa: intl.formatMessage({
      id: 'lq1zb93v2YH5kl2-uBQf',
      defaultMessage: 'villas',
      description: 'Plural sale unit noun for villas in offer and pricing messages',
    }),
  }

  const result = known[saleUnit.toLowerCase()]

  if (result) {
    if (startsWithCapital) {
      return capitalise(result)
    }
    return result
  }

  // i18n: fallback for unknown sale units - uses getPlural (English-only)
  return getPlural(saleUnit)
}

/**
 * Returns a localized singular sale unit label (e.g. "/room", "total", "/person*").
 *
 * Use this when the UI needs the unit label by itself rather than a counted phrase.
 */
export function formatSaleUnitLabel(
  intl: IntlShape,
  saleUnit: string,
  options?: FormatSaleUnitLabelOptions,
): string
export function formatSaleUnitLabel(
  intl: IntlShape,
  saleUnit?: string,
  options?: FormatSaleUnitLabelOptions,
): string | undefined
export function formatSaleUnitLabel(
  intl: IntlShape,
  saleUnit?: string,
  options: FormatSaleUnitLabelOptions = {},
): string | undefined {
  if (!saleUnit) {
    return undefined
  }

  const hasLeadingSlash = saleUnit.startsWith('/')
  const hasAsterisk = saleUnit.endsWith('*')
  const strippedSaleUnit = saleUnit.replace(/^\//, '').replace(/\*$/, '')
  const normalizedSaleUnit = strippedSaleUnit.toLowerCase()
  const startsWithCapital = strippedSaleUnit.length > 0 &&
    strippedSaleUnit[0] === strippedSaleUnit[0].toUpperCase() &&
    strippedSaleUnit[0] !== strippedSaleUnit[0].toLowerCase()

  const localizedSaleUnit = (() => {
    switch (normalizedSaleUnit) {
      case 'adult':
        return intl.formatMessage({
          id: '-f206TRRuHyl-LIHwsRx',
          defaultMessage: 'adult',
          description: 'Price sale unit label for an adult in pricing rows and compact upsell tiles',
        })
      case 'cabin':
        return intl.formatMessage({
          id: 'fnsTfgUBU1VIH5-qEIdh',
          defaultMessage: 'cabin',
          description: 'Price sale unit label for a cabin in pricing rows and compact upsell tiles',
        })
      case 'day':
        return intl.formatMessage({
          id: 'IFJ4D_yXEBRWRSrrQJHC',
          defaultMessage: 'day',
          description: 'Price sale unit label for a day in pricing rows and compact upsell tiles',
        })
      case 'dayPerPerson':
        return intl.formatMessage({
          id: '2T-RP8a7ccp11ORnBeNb',
          defaultMessage: 'day per person',
          description: 'Per day per person sale unit label in the tours search result price stack',
        })
      case 'guest':
        return intl.formatMessage({
          id: 'tard3zgFlUM1c8Npfmki',
          defaultMessage: 'guest',
          description: 'Price sale unit label for a guest in pricing rows and compact upsell tiles',
        })
      case 'night':
        return intl.formatMessage({
          id: 'MURspPvTr5WlkK_VwwWP',
          defaultMessage: 'night',
          description: 'Price sale unit label for a night in pricing rows and compact upsell tiles',
        })
      case 'package':
        return intl.formatMessage({
          id: 'UAH6BoW9srE7wEKFxqcg',
          defaultMessage: 'package',
          description: 'Price sale unit label for a package in pricing rows and compact upsell tiles',
        })
      case 'person':
        return intl.formatMessage({
          id: 'a-c1CI8Lm4liycNIJk3a',
          defaultMessage: 'person',
          description: 'Price sale unit label for a person in pricing rows and compact upsell tiles',
        })
      case 'property':
        return intl.formatMessage({
          id: 'NbklkiFa1BwLLTtt7MGZ',
          defaultMessage: 'property',
          description: 'Price sale unit label for a property in pricing rows and compact upsell tiles',
        })
      case 'rental':
        return intl.formatMessage({
          id: 'MuvPvo_eOWU3WEihaHlX',
          defaultMessage: 'rental',
          description: 'Price sale unit label for a rental in pricing rows and compact upsell tiles',
        })
      case 'room':
        return intl.formatMessage({
          id: 'FCCpbvntIABECfA7su6D',
          defaultMessage: 'room',
          description: 'Price sale unit label for a room in pricing rows and compact upsell tiles',
        })
      case 'suite':
        return intl.formatMessage({
          id: 'UeCuDfDGQlce_E5_xFvG',
          defaultMessage: 'suite',
          description: 'Price sale unit label for a suite in pricing rows and compact upsell tiles',
        })
      case 'total':
        return intl.formatMessage({
          id: '76yZnATf5NI7FZT5OWdI',
          defaultMessage: 'total',
          description: 'Price sale unit label for a total price in pricing rows and compact upsell tiles',
        })
      case 'traveler':
        return intl.formatMessage({
          id: 'XpTz0bInxd712UrMo0rQ',
          defaultMessage: 'traveler',
          description: 'Price sale unit label for a traveler in pricing rows and compact upsell tiles',
        })
      case 'traveller':
        return intl.formatMessage({
          id: 't__SzkPvir7BRW1EniEC',
          defaultMessage: 'traveller',
          description: 'Price sale unit label for a traveller in pricing rows and compact upsell tiles',
        })
      case 'villa':
        return intl.formatMessage({
          id: 'eGYNilWcgIbb7fsLoht6',
          defaultMessage: 'villa',
          description: 'Price sale unit label for a villa in pricing rows and compact upsell tiles',
        })
      default:
        return strippedSaleUnit
    }
  })()

  const formattedSaleUnit = startsWithCapital ? capitalise(localizedSaleUnit) : localizedSaleUnit
  const withLeadingSlash = options.withLeadingSlash ?? hasLeadingSlash
  const shouldPrefixWithSlash = withLeadingSlash && !/^(total|with)/.test(normalizedSaleUnit)

  if (shouldPrefixWithSlash && hasAsterisk) {
    return intl.formatMessage(
      {
        id: '3oJZ7LjD4YYy7YI6cJRT',
        defaultMessage: '/{saleUnit}*',
        description: 'Decorated sale unit label with a leading slash and trailing asterisk in pricing rows and compact upsell tiles',
      },
      { saleUnit: formattedSaleUnit },
    )
  }

  if (shouldPrefixWithSlash) {
    return intl.formatMessage(
      {
        id: 'upBf5lM27l0oOdNxNEt5',
        defaultMessage: '/{saleUnit}',
        description: 'Decorated sale unit label with a leading slash in pricing rows and compact upsell tiles',
      },
      { saleUnit: formattedSaleUnit },
    )
  }

  if (hasAsterisk) {
    return intl.formatMessage(
      {
        id: 'dboISEuDUI1RFhNEvZp_',
        defaultMessage: '{saleUnit}*',
        description: 'Decorated sale unit label with a trailing asterisk in pricing rows and compact upsell tiles',
      },
      { saleUnit: formattedSaleUnit },
    )
  }

  return formattedSaleUnit
}

interface SaleUnitFormattingFns {
  formatSaleUnitCount: (saleUnit: string, count: number) => string
  getSaleUnitPluralNoun: (saleUnit: string) => string
  formatSaleUnitLabel: FormatSaleUnitLabelFn
}

function useSaleUnitFormatting(): SaleUnitFormattingFns {
  const intl = useIntl()
  return useMemo(() => {
    function formatLocalizedSaleUnitLabel(
      saleUnit: string,
      options?: FormatSaleUnitLabelOptions,
    ): string
    function formatLocalizedSaleUnitLabel(
      saleUnit?: string,
      options?: FormatSaleUnitLabelOptions,
    ): string | undefined
    function formatLocalizedSaleUnitLabel(
      saleUnit?: string,
      options: FormatSaleUnitLabelOptions = {},
    ): string | undefined {
      return formatSaleUnitLabel(intl, saleUnit, options)
    }

    return {
      formatSaleUnitCount: (saleUnit: string, count: number) => formatSaleUnitCount(intl, saleUnit, count),
      getSaleUnitPluralNoun: (saleUnit: string) => getSaleUnitPluralNoun(intl, saleUnit),
      formatSaleUnitLabel: formatLocalizedSaleUnitLabel,
    }
  }, [intl])
}

export default useSaleUnitFormatting
